import { isValidEmail, isValidPhoneFallback } from './inlineRegistration.js';
import { PhoneMask } from './phoneMask.js';
import { getAdvertisingParameters } from './advertisingAttribution.js';
import { Fetch } from './helpers.js';

const UTM_KEYS = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term'];

const setFieldError = (field, message = '') => {
  const input = field.querySelector('input:not([type="hidden"]), textarea');
  const control = field.querySelector('.js-consultation-control');
  const error = field.querySelector('.inline-registration__error');
  const invalid = Boolean(message);

  field.classList.toggle('inline-registration__field--invalid', invalid);
  input.setAttribute('aria-invalid', String(invalid));
  if (control && control !== input) control.setAttribute('aria-invalid', String(invalid));
  error.textContent = message;
};

const initForm = (form) => {
  const name = form.elements.name;
  const email = form.elements.email;
  const phone = form.elements.phone;
  const message = form.elements.message;
  const privacy = form.elements.privacy;
  // HTML minification removes the redundant type="submit" from buttons.
  const submit = form.querySelector('.inline-registration__submit');
  const submitError = form.querySelector('.inline-registration__submit-error');
  const dialog = form.parentElement.querySelector('.js-consultation-dialog');
  const errors = form.dataset;
  const phoneMask = window.intlTelInput
    ? new PhoneMask(phone, {
      showValidation: false
    })
    : null;
  let submitting = false;

  const field = (input) => input.closest('.js-consultation-field');
  const validateName = () => {
    const error = name.value.trim() ? '' : errors.errorRequired;
    setFieldError(field(name), error);
    return !error;
  };
  const validateEmail = () => {
    const value = email.value.trim();
    const error = !value ? errors.errorRequired : (isValidEmail(value) ? '' : errors.errorEmail);
    setFieldError(field(email), error);
    return !error;
  };
  const validatePhone = () => {
    const value = phone.value.trim();
    const valid = !value || (phoneMask ? phoneMask.validate(false) : isValidPhoneFallback(value));
    setFieldError(field(phone), valid ? '' : errors.errorPhone);
    return valid;
  };
  const validateMessage = () => {
    const valid = message.value.length <= 1000;
    setFieldError(field(message), valid ? '' : errors.errorMessageLength);
    return valid;
  };
  const validatePrivacy = () => {
    const error = privacy.checked ? '' : errors.errorPrivacy;
    setFieldError(field(privacy), error);
    return !error;
  };

  [[name, validateName], [email, validateEmail], [phone, validatePhone], [message, validateMessage]]
    .forEach(([input, validate]) => {
      input.addEventListener('input', validate);
      input.addEventListener('blur', validate);
    });
  privacy.addEventListener('change', validatePrivacy);

  dialog.querySelector('.js-consultation-dialog-close').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submitting) return;

    const valid = [validateName(), validateEmail(), validatePhone(), validateMessage(), validatePrivacy()].every(Boolean);
    if (!valid) {
      form.querySelector('input[aria-invalid="true"], textarea[aria-invalid="true"]')?.focus();
      return;
    }

    const payload = {
      name: name.value.trim(),
      email: email.value.trim(),
      phone: phone.value.trim() && phoneMask
        ? (phoneMask.getFormattedNumber() || phone.value.trim())
        : phone.value.trim(),
      message: message.value,
      page_id: form.elements.page_id.value,
      component_id: form.elements.component_id.value
    };
    const params = getAdvertisingParameters();
    UTM_KEYS.forEach((key) => {
      if (params[key]) payload[key] = params[key];
    });

    submitting = true;
    submit.disabled = true;
    submitError.hidden = true;
    try {
      const response = await Fetch(form.action, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        if (response.status === 400) {
          const details = await response.json();
          [name, email, phone, message].forEach((input) => {
            if (details[input.name]?.length) setFieldError(field(input), details[input.name][0]);
          });
        }
        throw new Error(`Feedback request failed: ${response.status}`);
      }

      window.CargoTetrisAnalytics?.track('generate_lead');
      form.reset();
      dialog.showModal();
    } catch (error) {
      submitError.hidden = false;
      console.error(error);
    } finally {
      submitting = false;
      submit.disabled = false;
    }
  });
};

export default function consultationFeedback () {
  document.querySelectorAll('.js-consultation-feedback').forEach(initForm);
}
